import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {UserSubscription, UserSubscriptionType} from "../../../user/user-subscription";
import {Config} from "../../../config/config";
import * as fromStore from "../../../store";
import {filter, first, map, switchMap, takeUntil} from "rxjs/operators";
import {combineLatest} from 'rxjs';
import {Store} from "@ngrx/store";
import {User} from "../../../user/user";
import {clientRoles} from "../../../user/user-roles-actions";
import {UserSubscriptionStatus} from "../../../user/user-subscription-status";
import {IPlanInfo} from "./plan-info.interface";
import {Subject} from "rxjs";
import {Website} from "../../../websites/website/website";
import {AiBuilderService} from "../../../websites/website/ai-builder/service/ai-builder.service";
import {CloudflareService} from "../../../websites/website/booster/services/cloudflare.service";
import {PaymentDialogComponent} from "../../billing/payment-dialog/payment-dialog.component";
import {DialogService} from "../../../../ng2-bootstrap-modal/dialog.service";
import {of} from "rxjs";
import {Observable} from "rxjs";
import {Utils} from "../../../shared/utils/utils";
import {DbModalService} from "../../../modules/modal/modal";
import {
  DedicatedVmContactComponent
} from "../../../components/common/dedicated-vm-contact-dialiog/dedicated-vm-contact.component";
import {PaymentMethod} from "../../../user/payment-method";
import {ApiProService} from "../../../shared/services/api-pro/api-pro.service";

@Component({
    selector: 'db-alternative-subscription-plan',
    templateUrl: './alternative-subscription-plan.component.html',
    styleUrls: ['./alternative-subscription-plan.component.scss'],
    standalone: false
})
export class AlternativeSubscriptionPlanComponent implements OnInit, OnDestroy {
  @Input() userSubscription: UserSubscription;
  @Input() user: User;
  @Input() hasUpgrade: boolean;
  @Input() annualPrice: number;
  @Input() isDemoUser: boolean;
  @Input() activationClicked: boolean;
  @Input() currentWorkspaceRole: number;
  @Input() paymentMethods: PaymentMethod[];

  @Output() unpause?: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() cancelSub: EventEmitter<void> = new EventEmitter<void>();
  @Output() renewFromDomainBanner?: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() activateUserSubscription?: EventEmitter<UserSubscription> = new EventEmitter<UserSubscription>();
  @Output() retryUserSubscription?: EventEmitter<UserSubscription> = new EventEmitter<UserSubscription>();
  @Output() onAddPaymentMethod?: EventEmitter<boolean> = new EventEmitter<boolean>();
  protected readonly userSubscriptionStatus = UserSubscriptionStatus;
  protected readonly workspaceRoles = clientRoles;
  public twCareLink: string = Config.twCareLink;
  public visitsInfo: IPlanInfo;
  public backupStorageInfo: IPlanInfo;
  public hostingStorageInfo: IPlanInfo;
  public websitesInfo: IPlanInfo;
  vibeCodingInfo: IPlanInfo;
  public extraWebsitesInfo: IPlanInfo;
  public bandwidthInfo: IPlanInfo;
  public generatedWebsitesInfo: IPlanInfo;
  public loading = true;
  isMobile = window.innerWidth <= 991;

  public totalHostingVisits: number;
  public totalBandwidth: number;
  public usedHostingStorage: number;
  public usedBackupStorage: number;
  public usedBackupStorageReal: number;
  public totalBackupStorage: number;
  public usedBackupStorageInPercent: number;
  public usedHostingStorageInPercent: number;
  public usedHostingVisitsInPercent: number;
  public usedHostingStorageReal: number;
  public usedHostingVisits: number;
  public usedHostingVisitsReal: number;
  public totalHostingStorage: number;
  public totalHostingStorageUnit: string;
  public hostingsLimit: number;
  public userLocalStorage: any;

  protected readonly UserSubscriptionType = UserSubscriptionType;
  protected readonly clientRoles = clientRoles;
  protected readonly UserSubscriptionStatus = UserSubscriptionStatus;
  private _destroyStream$: Subject<void> = new Subject<void>();

  constructor(private _store: Store<fromStore.DbStoreState>,
              private _cloudflareService: CloudflareService,
              private _dialogService: DialogService,
              private _modalService: DbModalService,
              private _aiBuilderService: AiBuilderService,
              private _apiProService: ApiProService) {
  }

  ngOnInit() {
    this._getPlanStats();
    this._store.select(fromStore.getWebsites).subscribe((websites: Website[]) => {
      if (websites?.length) {
        this._dispatchPlanInfo();
      }
    });
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUserLocalStorage)),
        first()
      )
      .subscribe((userLocalStorage: any) => {
        this.userLocalStorage = userLocalStorage;
      });
  }

  private _getApiProStats(subs: UserSubscription) {
    if (this.userSubscription?.apiResellerPro) {
      this._apiProService.getApiProStats(this.user, subs)
        .pipe(takeUntil(this._destroyStream$))
        .subscribe((apiProStats) => {
          if (apiProStats) {
            this.websitesInfo = apiProStats.websitesInfo;
            this.extraWebsitesInfo = apiProStats.extraWebsitesInfo;
            this.generatedWebsitesInfo = apiProStats.generatedWebsitesInfo;
          }
        });
    }
  }

  private _getPlanStats() {
    this._store.select(fromStore.getUserSubscription)
      .pipe(
        switchMap((subs) => combineLatest(...this.getSelectorList(subs))),
        switchMap((arr) => combineLatest(...this._addBandwidthObservable(arr))),
        takeUntil(this._destroyStream$)
      ).subscribe(([websites, generatedWebsiteCount, hostingResources, hostingVisits, backupLimits, vibeLimits, bandwidth]) => {
      this._setStorageTotalValues();
      let hostedWebsites;
      if (websites) {
        const websitesCount: number = this.user && this.user.hosting_services && parseInt(this.user.hosting_services.domain_count.parameter_value, 10);
        hostedWebsites = (websites as Website[]).filter((website: Website) => !website.parentId && website.isActive && website.type === 'live' && (!!website.domain_hash || website.connectedFrom === 'speed_optimizer')).length;

        this.websitesInfo = {
          total: websitesCount,
          real: hostedWebsites,
          percent: websitesCount > 0 ? Math.min(100, Math.round((hostedWebsites / websitesCount) * 100)) : 0
        };

        if (this.userSubscription?.plan?.services?.wvc) {
          const alreadyUsed = vibeLimits?.edit_limitations?.already_used;
          const alreadyUsedVibeCreated = vibeLimits?.create_limitations?.already_used;
          const totalVibeLimitationCreated = vibeLimits?.create_limitations?.limitation;
          const totalVibeLimitation = vibeLimits?.edit_limitations?.limitation;
          const totalAdditionalCredits = vibeLimits?.additional_credits?.total_credits || 0;

          // If already_used equals total_credits, set usedVibeLimitation to 0
          const usedVibeLimitation = alreadyUsed === totalAdditionalCredits ? 0 : alreadyUsed;

          // Get the newest expiration date from credit_data
          let additionalCreditsExpDate = null;
          if (vibeLimits?.additional_credits?.credit_data && Array.isArray(vibeLimits.additional_credits.credit_data)) {
            const expDates = vibeLimits.additional_credits.credit_data
                .map(item => item.expiration_date)
                .filter(date => date)
                .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
            additionalCreditsExpDate = expDates.length > 0 ? expDates[0] : null;
          }

          const usedVibeInPercent = (usedVibeLimitation / (totalVibeLimitation + +totalAdditionalCredits)) * 100;

          this.vibeCodingInfo = {
            createdReal: alreadyUsedVibeCreated,
            createdTotal: totalVibeLimitationCreated,
            createdPercent: (alreadyUsedVibeCreated / (totalVibeLimitationCreated)) * 100,
            real: usedVibeLimitation,
            total: totalVibeLimitation,
            totalAdditionalCredits: totalAdditionalCredits,
            additionalCreditsExpDate: additionalCreditsExpDate,
            percent: usedVibeInPercent
          };
        }

      }

      if (this.userSubscription?.apiResellerPro && this.userSubscription?.plan?.hasMultiplierService) {
        this._getApiProStats(this.userSubscription);
      } else {
        if ((generatedWebsiteCount || generatedWebsiteCount === 0) && this.userSubscription.plan.services.ai_builder) {
          const regenerationWebsitesCount = parseInt(this.userSubscription.plan.services.ai_builder.regeneration_count.parameter_value, 10);
          const regenerationAdditionalWebsitesCount = parseInt(
              this.userSubscription.plan.additionalDomainCount &&
              this.userSubscription.plan.services.ai_builder.regeneration_additional_count ?
                this.userSubscription.plan.services.ai_builder.regeneration_additional_count.parameter_value : 0, 10)
            * this.userSubscription.plan.additionalDomainCount;
          const totalWebsitesCount = regenerationWebsitesCount + regenerationAdditionalWebsitesCount;

          this.generatedWebsitesInfo = {
            total: totalWebsitesCount,
            real: generatedWebsiteCount as number,
            percent: ((generatedWebsiteCount as number) / totalWebsitesCount) * 100
          };
        }
      }

      if (hostingVisits || this.userSubscription.isExpired || this.userSubscription.recharge || hostedWebsites === 0) {
        this.usedHostingVisitsReal = hostingVisits ? hostingVisits['visitors'] : 0;
        // set 5% of total for progress bar to show data
        this.usedHostingVisits = this._countOptimalStatValue(this.totalHostingVisits, this.usedHostingVisitsReal);
        this.usedHostingVisitsInPercent = this.usedHostingVisits / this.totalHostingVisits * 100;

        this.visitsInfo = {
          real: this.usedHostingVisitsReal,
          total: this.totalHostingVisits,
          percent: this.usedHostingVisitsInPercent
        };
      }

      if ((hostingResources || this.userSubscription.isExpired || this.userSubscription.recharge || hostedWebsites === 0) && this.totalHostingStorage) {
        this.usedHostingStorageReal = hostingResources ? hostingResources['storage'] : 0;
        this.usedHostingStorage = this._countOptimalStatValue(this.totalHostingStorage, this.usedHostingStorageReal / (1024 * 1024));
        this.usedHostingStorageInPercent = (this.usedHostingStorage / this.totalHostingStorage) * 100;
        this.hostingStorageInfo = {
          real: this.usedHostingStorageReal,
          total: this.totalHostingStorage,
          percent: this.usedHostingStorageInPercent,
          unit: this.totalHostingStorageUnit
        };
      }

      if (backupLimits || this.userSubscription.isExpired || this.userSubscription.recharge) {
        const availableStorage = backupLimits ? backupLimits['available_storage'] : 0;
        this.totalBackupStorage = this.user && this.user.backup_services ? this.user.additionalWebsiteCount ? (parseInt(availableStorage, 10) + (parseInt(this.user.additionalWebsiteCount, 10) * parseInt(this.user.backup_services.backup_additional_storage ? this.user.backup_services.backup_additional_storage.parameter_value : 1, 10) * 1024 * 1024)) : availableStorage : availableStorage;
        this.usedBackupStorageReal = backupLimits ? backupLimits['used_storage'] : 0;
        this.usedBackupStorage = this._countOptimalStatValue(this.totalBackupStorage, this.usedBackupStorageReal);
        this.usedBackupStorageInPercent = (this.usedBackupStorage / this.totalBackupStorage) * 100;
        this.backupStorageInfo = {
          real: this.usedBackupStorageReal,
          total: this.totalBackupStorage,
          percent: this.usedBackupStorageInPercent
        };
      }

      if (bandwidth) {
        this.bandwidthInfo = {} as IPlanInfo;
        if (!this.totalBandwidth) {
          this.bandwidthInfo.show = false;
        } else {
          const usedBandwidth = this._countOptimalStatValue(this.totalBandwidth, +bandwidth / (1024 * 1024));
          this.bandwidthInfo.percent = (usedBandwidth / +bandwidth) * 100;
          this.bandwidthInfo.real = usedBandwidth;
          this.bandwidthInfo.total = this.totalBandwidth;
        }
      }
      this.loading = false;
    });
  }

  private _setStorageTotalValues() {
    this.totalHostingVisits = this.user && this.user.hosting_services && this.userSubscription && this.userSubscription.plan ?
      this.user.additionalWebsiteCount ?
        (parseInt(this.user.hosting_services.hosting_visits.parameter_value, 10) +
          ((parseInt(this.user.additionalWebsiteCount, 10) ||
              this.userSubscription.plan.additionalDomainCount) *
            parseInt(this.user.hosting_services.hosting_additional_visits ? this.user.hosting_services.hosting_additional_visits.parameter_value : 0, 10))) :
        (this.userSubscription.plan.additionalDomainCount ? this.userSubscription.plan.additionalDomainCount * parseInt(this.user.hosting_services.hosting_visits.parameter_value, 10) :
          parseInt(this.user.hosting_services.hosting_visits.parameter_value, 10)) : null;
    this.totalHostingStorage = this.user && this.user.hosting_services && (parseInt(this.user.hosting_services?.hosting_storage.parameter_value, 10) +
      ((parseInt(this.user.additionalWebsiteCount, 10) ||
          this.userSubscription.plan.additionalDomainCount) *
        parseInt(this.user.hosting_services.hosting_additional_storage ? this.user.hosting_services.hosting_additional_storage.parameter_value : 1, 10)));
    this.totalHostingStorageUnit = this.user?.hosting_services?.hosting_storage.unit;
    this.hostingsLimit = parseInt(this.user?.hosting_services?.domain_count.parameter_value, 10) + this.user?.additionalWebsiteCount;
    if (this.userSubscription?.plan?.services?.cloudflare_cdn && this.userSubscription?.plan?.services.cloudflare_cdn.cdn_bandwidth) {
      this.totalBandwidth = this.user?.additionalWebsiteCount ? ((parseInt(this.userSubscription?.plan?.services?.cloudflare_cdn.cdn_bandwidth.parameter_value, 10) + (parseInt(this.user?.additionalWebsiteCount, 10)) *
          parseInt(this.userSubscription?.plan?.services.cloudflare_cdn.cdn_additional_bandwidth.parameter_value, 10))) :
        (this.userSubscription?.plan?.additionalDomainCount ? this.userSubscription?.plan?.additionalDomainCount * parseInt(this.userSubscription?.plan?.services?.cloudflare_cdn.cdn_bandwidth.parameter_value, 10) : parseInt(this.userSubscription?.plan?.services?.cloudflare_cdn.cdn_bandwidth.parameter_value, 10));
    }
  }

  private _dispatchPlanInfo() {
    const demoUser = Utils.isDemoUser(this.userSubscription, this.user);
    if (this.userSubscription && !this.userSubscription.isExpired) {
      this._store.dispatch(new fromStore.LoadHostingVisits(demoUser ? Config.demoDomainId : null));
      this._store.dispatch(new fromStore.LoadGeneratedWebsiteCount());
      this._store.dispatch(new fromStore.LoadHostingResources(demoUser ? Config.demoDomainId : null));
    }
  }

  private _countOptimalStatValue(total: number, used: number): number {
    const usedPercent: number = (used / total * 100);
    return usedPercent < 5 && usedPercent !== 0 ? (5 * total) / 100 : used;
  }

  private getSelectorList(userSubs: UserSubscription) {
    if (userSubs?.isExpired || userSubs?.recharge) {
      return [
        this._store.select(fromStore.getWebsites),
        this._store.select(fromStore.getGeneratedWebsitesCount)
      ];
    }

    const allSelectors = [
      this._store.select(fromStore.getWebsites),
      this._store.select(fromStore.getGeneratedWebsitesCount),
      this._store.select(fromStore.getHostingResources),
      this._store.select(fromStore.getHostingVisits),
      this._store.select(fromStore.getBackupLimits),
      this._store.select(fromStore.getVibeLimitationsData),
    ];

    return allSelectors;
  }

  private _addBandwidthObservable(selectorValues: any[]): Observable<any>[] {
    const websites = selectorValues[0];
    const observables = selectorValues.map(value => of(value));

    if (websites && websites.length && this._checkCloudflare()) {
      const hasEnabledCloudflare = websites.find((website) => website.cloudflareEnabled);
      if (hasEnabledCloudflare) {
        observables.push(this._cloudflareService.getBandwidth()
          .pipe(map(resp => {
            if (resp && resp['success']) {
              return +resp['data'].value;
            }
            return null;
          })));
      }
    }

    return observables;
  }

  private _checkCloudflare = () => (this.userSubscription.plan.services.cloudflare_cdn &&
    (+this.userSubscription.plan.services.cloudflare_cdn.cdn_bandwidth.parameter_value ||
      +this.userSubscription.plan.services.cloudflare_cdn.cdn_additional_bandwidth.parameter_value))

  public showContactSales(): void {
    this._modalService.open(DedicatedVmContactComponent, {hasCloseBtn: true, height: 208, width: 500});
  }

  ngOnDestroy() {
    this._destroyStream$.next();
    this._destroyStream$.complete();
  }

}
